/**
 * GentleMovement — Arrival + optional guided movement
 * 
 * Begins with a soft 10-15s "Arrival Moment" — no counting, no effort.
 * Then offers optional gentle movements.
 * One movement per screen. No reps. No intensity. Only within comfort.
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';

const SPINAL_MOVEMENTS = [
  {
    id: 'pelvic-tilt',
    name: 'Pelvic Tilt',
    position: 'Lying on your back, knees bent',
    lines: [
      'Gently allow your lower back',
      'to move toward the floor\u2026',
      '',
      'and slowly release',
      '',
      'Move with your breath',
    ],
    note: 'There is no perfect way\njust a gentle softening',
  },
  {
    id: 'knee-to-chest',
    name: 'Single Knee to Chest',
    position: 'Lying on your back',
    lines: [
      'Slowly draw one knee',
      'toward your chest',
      '',
      'Hold softly\u2026',
      '',
      'Let the other leg rest',
      '',
      'Switch whenever it feels natural',
    ],
    note: 'Let your body guide the movement',
  },
  {
    id: 'seated-lean',
    name: 'Seated Forward Lean',
    position: 'Sitting on the edge of a chair',
    lines: [
      'Rest your hands on your thighs',
      '',
      'Slowly lean forward\u2026',
      '',
      'allowing your head',
      'to hang softly',
      '',
      'Breathe into the space you create',
    ],
    note: 'Even the smallest movement\ncreates space',
  },
];

// Arrival text sequence
const ARRIVAL_TEXTS = [
  { text: 'Take a moment\u2026', delay: 1200, duration: 3500 },
  { text: 'Let the breath come naturally', delay: 5500, duration: 3500 },
  { text: 'Nothing to do', delay: 10000, duration: 3000 },
];
const ARRIVAL_DURATION = 14000;

const GentleMovement = ({ condition, onComplete, onSkip }) => {
  const [phase, setPhase] = useState('arrival'); // 'arrival' | 'choice' | number (movement index)
  const [arrivalText, setArrivalText] = useState('');
  const arrivalTimersRef = useRef([]);

  // Arrival moment — soft text sequence, no structure, no effort
  useEffect(() => {
    if (phase !== 'arrival') return;

    const timers = [];

    ARRIVAL_TEXTS.forEach(({ text, delay, duration }) => {
      const showTimer = setTimeout(() => setArrivalText(text), delay);
      const hideTimer = setTimeout(() => setArrivalText(''), delay + duration);
      timers.push(showTimer, hideTimer);
    });

    // Auto-transition to choice
    const transitionTimer = setTimeout(() => setPhase('choice'), ARRIVAL_DURATION);
    timers.push(transitionTimer);

    arrivalTimersRef.current = timers;
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  const handleNext = () => {
    if (typeof phase !== 'number') return;
    if (phase >= SPINAL_MOVEMENTS.length - 1) {
      onComplete();
    } else {
      setPhase(phase + 1);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center"
      style={{ background: 'linear-gradient(180deg, #0a0e0a 0%, #0d120d 50%, #0a0e0a 100%)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2, ease: 'easeInOut' }}
      data-testid="gentle-movement"
    >
      <AnimatePresence mode="wait">
        {/* === ARRIVAL MOMENT === */}
        {phase === 'arrival' && (
          <motion.div
            key="arrival"
            className="flex flex-col items-center justify-center z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            data-testid="arrival-moment"
          >
            {/* Soft ambient pulse — no circle, just a gentle glow */}
            <motion.div
              className="w-32 h-32 rounded-full mb-10 pointer-events-none"
              style={{
                background: 'radial-gradient(circle, rgba(140,170,120,0.04) 0%, transparent 70%)',
              }}
              animate={{
                scale: [1, 1.15, 1],
                opacity: [0.4, 0.7, 0.4],
              }}
              transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
            />

            {/* Arrival text — fades in and out */}
            <div className="h-12 flex items-center justify-center">
              <AnimatePresence mode="wait">
                {arrivalText && (
                  <motion.p
                    key={arrivalText}
                    className="text-white/30 text-sm text-center"
                    style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 1.2, ease: 'easeInOut' }}
                  >
                    {arrivalText}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}

        {/* === CHOICE SCREEN === */}
        {phase === 'choice' && (
          <motion.div
            key="choice"
            className="text-center px-8 z-10"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
          >
            <p className="text-white/25 text-xs tracking-[0.2em] uppercase mb-8"
              style={{ fontFamily: "'Manrope', sans-serif" }}>
              Gentle Movement
            </p>

            <p className="text-white/50 text-lg mb-3"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
              Would you like some gentle guidance?
            </p>

            <p className="text-white/25 text-sm mb-12 max-w-xs mx-auto"
              style={{ fontFamily: "'Manrope', sans-serif" }}>
              Three soft movements to create space in your spine
            </p>

            <div className="flex flex-col gap-4 items-center">
              <motion.button
                onClick={() => setPhase(0)}
                className="w-64 py-3.5 rounded-full text-sm text-white/55"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  background: 'rgba(120,150,100,0.12)',
                  border: '1px solid rgba(140,170,120,0.15)',
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                data-testid="movement-guide-btn"
              >
                Guide me through gentle movement
              </motion.button>

              <motion.button
                onClick={onSkip}
                className="w-64 py-3 rounded-full text-sm text-white/25"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.04)',
                }}
                whileTap={{ scale: 0.97 }}
                data-testid="movement-skip-btn"
              >
                Skip for now
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* === MOVEMENT SCREENS === */}
        {typeof phase === 'number' && (
          <motion.div
            key={`movement-${phase}`}
            className="text-center px-8 z-10 max-w-md"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center gap-2 mb-10">
              {SPINAL_MOVEMENTS.map((_, i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full transition-all duration-500"
                  style={{
                    background: i <= phase ? 'rgba(140,170,120,0.4)' : 'rgba(255,255,255,0.08)',
                  }}
                />
              ))}
            </div>

            <p className="text-white/25 text-xs tracking-[0.15em] uppercase mb-6"
              style={{ fontFamily: "'Manrope', sans-serif" }}>
              {phase + 1} of {SPINAL_MOVEMENTS.length}
            </p>

            <h2 className="text-white/65 text-2xl mb-3"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
              {SPINAL_MOVEMENTS[phase].name}
            </h2>

            <p className="text-white/30 text-xs uppercase tracking-wider mb-8"
              style={{ fontFamily: "'Manrope', sans-serif" }}>
              {SPINAL_MOVEMENTS[phase].position}
            </p>

            {/* Poetic instruction lines */}
            <div className="mb-10 max-w-sm mx-auto space-y-1">
              {SPINAL_MOVEMENTS[phase].lines.map((line, j) => (
                <p key={j}
                  className={line ? 'text-white/40 text-sm leading-relaxed' : 'h-3'}
                  style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {line}
                </p>
              ))}
            </div>

            {/* Permission note */}
            <motion.div
              className="mb-12"
              animate={{ opacity: [0.15, 0.25, 0.15] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              {SPINAL_MOVEMENTS[phase].note.split('\n').map((line, j) => (
                <p key={j} className="text-white/20 text-xs italic"
                  style={{ fontFamily: "'Playfair Display', serif" }}>
                  {line}
                </p>
              ))}
            </motion.div>

            <div className="flex justify-center gap-4">
              <motion.button
                onClick={handleNext}
                className="flex items-center gap-2 px-8 py-3 rounded-full text-sm text-white/50"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  background: 'rgba(120,150,100,0.12)',
                  border: '1px solid rgba(140,170,120,0.15)',
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                data-testid="movement-next-btn"
              >
                {phase >= SPINAL_MOVEMENTS.length - 1 ? 'Continue' : 'Next'} <ArrowRight size={14} />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Skip from movement screens */}
      {typeof phase === 'number' && (
        <motion.button
          onClick={onComplete}
          className="absolute top-6 right-6 p-2.5 rounded-full z-20"
          style={{ background: 'rgba(255,255,255,0.05)' }}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <X size={16} className="text-white/20" />
        </motion.button>
      )}

      <div className="absolute bottom-6 px-8 text-center">
        <p className="text-white/10 text-[10px]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          This experience supports relaxation alongside your medical care.
        </p>
      </div>
    </motion.div>
  );
};

export default GentleMovement;
